import { ChangeDetectorRef, Component, inject, Input, input, OnChanges, output, SimpleChanges } from '@angular/core';
import { TimeRange, ZiroTimeRangeFormComponent } from '../ziro-time-range-form.component';
import { ZiroFormsModule } from '../../../ziro-forms.module';
import { ZiroFormAbstractDirective } from '../../../ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../ziro-form-state.service';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../ziro-forms-models';
import { cloneDeep, isEqual } from 'lodash';
import { Observable, of } from 'rxjs';

export type TimeRangeFormData = { [key: `slot${number}`]: TimeRange };

@Component({
  selector: 'ziro-time-ranges-form',
  templateUrl: './ziro-time-ranges-form.component.html',
  imports: [ZiroFormsModule, ZiroTimeRangeFormComponent],
  standalone: true,
})
export class ZiroTimeRangesFormComponent
  extends ZiroFormAbstractDirective<TimeRange[], TimeRangeFormData>
  implements OnChanges
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _changeDetectorRef = inject(ChangeDetectorRef);

  upstreamEntity = input<TimeRange[]>();
  dataAutomation = input.required<string>();
  hideDelete = input(false);

  @Input() isFormSubmitted: boolean;

  readonly rangeDelete = output<void>();

  fieldIds: `slot${number}`[] = [];
  safeEntity: TimeRange[] = [];
  formConfig: ZiroFormConfig = { fields: {} };

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes['upstreamEntity']?.currentValue) {
      this._updateFromUpstream(changes['upstreamEntity'].currentValue);
    }

    if (changes['isFormSubmitted']?.currentValue) {
      this._setIsFormSubmitted(true);
      this.fieldComponents?.forEach((field) => {
        field.showValidation = true;
      });
      this._triggerFieldValidation();
    }
  }

  getFormValidationState(): ZiroFormsValidationState {
    if (
      this.validationState === ZiroFormsValidationState.INVALID ||
      this._hasIncompleteRanges() ||
      this._hasOverlappingRanges()
    ) {
      return ZiroFormsValidationState.INVALID;
    }

    return this.validationState;
  }

  addItem(): void {
    const newFieldId = this._nextFieldId();
    this.fieldIds = [...this.fieldIds, newFieldId];
    this.formConfig.fields[newFieldId] = {};

    const newEntity = [...this.safeEntity, { start: '', end: '' }];
    this.entity = [...newEntity];
    this.safeEntity = [...newEntity];
    this.entitySource.next([...newEntity]);

    this._changeDetectorRef.detectChanges();

    if (this.isFormSubmitted) {
      this._setIsFormSubmitted(true);
      this.fieldComponents?.forEach((field) => {
        field.showValidation = true;
      });
    }
    this._triggerFieldValidation();
  }

  deleteItem(index: number): void {
    const removedId = this.fieldIds[index];
    this.fieldIds = this.fieldIds.filter((_, i) => i !== index);
    const newEntity = this.safeEntity.filter((_, i) => i !== index);
    this.entity = [...newEntity];
    this.safeEntity = [...newEntity];
    delete this.formData[removedId];
    delete this.formConfig.fields[removedId];
    this.entitySource.next([...newEntity]);
    this._triggerFieldValidation();
    this.rangeDelete.emit();
  }

  onRangeUpdate(update: ZiroFormsUpdate<TimeRange>, index: number): void {
    const current = this.safeEntity[index];
    const updatedRange: TimeRange = {
      start: update.new.start || current?.start || '',
      end: update.new.end || current?.end || '',
    };

    const newEntity = this.safeEntity.map((range, i) => (i === index ? updatedRange : range));
    if (isEqual(this.safeEntity, newEntity)) {
      return;
    }

    this.entity = [...newEntity];
    this.safeEntity = [...newEntity];
    this.entitySource.next([...newEntity]);

    this._triggerFieldValidation();
  }

  isOverlappingAtIndex(index: number): boolean {
    const range = this.safeEntity[index];
    if (!range?.start || !range?.end) {
      return false;
    }
    return this.safeEntity.some((r, i) => i !== index && !!r.start && !!r.end && this._rangesOverlap(range, r));
  }

  toEntity = (formData: TimeRangeFormData): TimeRange[] => Object.values(formData);

  toFormData = (entity: TimeRange[]): TimeRangeFormData => {
    const result = {} as TimeRangeFormData;
    (entity ?? []).forEach((value, index) => {
      let id = this.fieldIds[index];

      if (!id) {
        id = this._nextFieldId();
        this.fieldIds = [...this.fieldIds, id];
        this.formConfig.fields[id] = {};
      }
      result[id] = value;
    });

    return result;
  };

  private _hasIncompleteRanges(): boolean {
    return this.safeEntity.some((range) => !range.start || !range.end);
  }

  private _nextFieldId(): `slot${number}` {
    const ids = this.fieldIds.map((id) => Number(id.slice(4)));
    const next = ids.length ? Math.max(...ids) + 1 : 0;
    return `slot${next}`;
  }

  private _updateFromUpstream(entity: TimeRange[]): void {
    while (this.fieldIds.length < entity.length) {
      const id = this._nextFieldId();
      this.fieldIds = [...this.fieldIds, id];
      this.formConfig.fields[id] = {};
    }

    const clonedEntity = cloneDeep(entity);

    this.entity = clonedEntity;
    this.safeEntity = clonedEntity;
    this.entitySource.next(cloneDeep(clonedEntity));
  }

  private _hasOverlappingRanges(): boolean {
    for (let i = 0; i < this.safeEntity.length; i++) {
      const a = this.safeEntity[i];
      if (!a.start || !a.end) {
        continue;
      }
      for (let j = i + 1; j < this.safeEntity.length; j++) {
        const b = this.safeEntity[j];
        if (!b.start || !b.end) {
          continue;
        }
        if (this._rangesOverlap(a, b)) {
          return true;
        }
      }
    }
    return false;
  }

  private _rangesOverlap(a: TimeRange, b: TimeRange): boolean {
    const toMinutes = (t: string): number => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    return toMinutes(a.start) < toMinutes(b.end) && toMinutes(b.start) < toMinutes(a.end);
  }

  private _triggerFieldValidation(): void {
    Object.values(this.fieldChannels ?? {}).forEach((fieldChannel) => fieldChannel.validateSource.next());
    this._changeDetectorRef.detectChanges();
  }

  protected submit(): Observable<null> {
    return of(null);
  }
}
